import { useCallback, useEffect, useState } from 'react';
import { useKnowledgeHub } from './use-knowledge-hub.hooks';
import { useLocation } from 'react-router-dom';
import { INTERNAL_ROUTING, ROUTES } from 'shared/constants';

export function useBreadcrumbs() {
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const location = useLocation();

  const {
    organizationId,
    workspaceId,
    workspacesList,
    organizationsList,
    parentCategory,
    getCategory,
    searchParams,
  } = useKnowledgeHub();

  // Helper to build path with specific params
  const buildPath = useCallback(
    ({ oid, wid, cid }) => {
      const params = new URLSearchParams(searchParams);
      // Clear hub-related params first
      params.delete(INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION);
      params.delete(INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE);
      params.delete(INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY);
      params.delete(INTERNAL_ROUTING.QUERY_PARAMS.HUB);

      // Set only the params we need
      if (oid) params.set(INTERNAL_ROUTING.QUERY_PARAMS.HUB_ORGANIZATION, oid);
      if (wid) params.set(INTERNAL_ROUTING.QUERY_PARAMS.HUB_WORKSPACE, wid);
      if (cid) params.set(INTERNAL_ROUTING.QUERY_PARAMS.HUB_CATEGORY, cid);

      return `${location.pathname}?${params.toString()}`;
    },
    [searchParams, location.pathname],
  );

  const buildBreadcrumbs = useCallback(async () => {
    const items = [];

    const isSingleWorkspace = workspacesList?.length === 1;

    // Organization breadcrumb
    if (organizationId) {
      const organization = organizationsList?.find(org => org.id === organizationId);
      if (organization) {
        items.push({
          title: organization.name,
          // When single workspace, go back to hub home instead of workspace list
          path: isSingleWorkspace ? ROUTES.KNOWLEDGE_HUB : buildPath({ oid: organizationId }),
        });
      }
    }

    // Workspace breadcrumb (skip when single workspace — already auto-selected)
    if (workspaceId && !isSingleWorkspace) {
      const workspace = workspacesList?.find(ws => ws.id === workspaceId);
      if (workspace) {
        items.push({
          title: workspace.name,
          path: buildPath({ oid: organizationId, wid: workspaceId }),
        });
      }
    }

    // Only show category breadcrumbs if we're in a workspace context
    if (workspaceId && parentCategory?.fullPath) {
      const pathParts = parentCategory.fullPath.split(',');
      // Get directory IDs: odd indices > 1 (3, 5, 7...)
      // slice(0, -1) removes the last element (current category)
      const directoryIds = pathParts.filter((_, i) => i % 2 === 1 && i > 1).slice(0, -1);

      // Fetch categories (getCategory handles caching internally)
      const categoryPromises = directoryIds.map(catId =>
        getCategory({ organizationId, workspaceId, categoryId: catId }),
      );
      const categories = await Promise.all(categoryPromises);

      categories.filter(Boolean).forEach(category => {
        items.push({
          title: category.title,
          path: buildPath({ oid: organizationId, wid: workspaceId, cid: category.id }),
        });
      });
    }

    return items;
  }, [
    organizationId,
    workspaceId,
    organizationsList,
    workspacesList,
    parentCategory,
    getCategory,
    buildPath,
  ]);

  useEffect(() => {
    buildBreadcrumbs().then(setBreadcrumbItems);
  }, [buildBreadcrumbs]);

  return { breadcrumbItems };
}
